/**
 * Verschlüsselung des Check-in-Bereichs — AES-GCM, Schlüssel via PBKDF2.
 *
 * Verbindlich aus CLAUDE.md: Passnummer, Versicherungspolice, Notfallkontakt und
 * medizinische Angaben dürfen **niemals** im Klartext in die Datenbank, in ein
 * Export-Bundle oder in einen Cloud-Ordner gelangen. Verschlüsselt wird nur
 * dieser Bereich — die Tauchgänge bleiben ohne Passworteingabe nutzbar.
 *
 * Entscheidungen und ihre Gründe:
 *
 * * **AES-GCM statt AES-CBC.** GCM erkennt Manipulation: Ein verändertes
 *   Geheimtext-Byte lässt das Entschlüsseln scheitern, statt Unsinn zu liefern.
 * * **600'000 PBKDF2-Runden** (SHA-256), die Empfehlung des OWASP für 2023.
 *   Das kostet auf dem Telefon eine gute Sekunde — einmal beim Entsperren.
 * * **Ein frischer Zufalls-IV je Verschlüsselung.** Ein wiederverwendeter IV
 *   bricht AES-GCM vollständig; deshalb wird er nie abgeleitet, immer gewürfelt.
 * * **Der Schlüssel wird nie gespeichert**, weder in IndexedDB noch im
 *   localStorage. Er lebt im Speicher, solange entsperrt ist.
 * * **Kein Zurücksetzen.** Ohne Passwort sind die Daten verloren. Das ist der
 *   Zweck, nicht ein Mangel — die App sagt es beim Einrichten deutlich.
 */

const runden = 600_000;
const SALZ_BYTES = 16;
const IV_BYTES = 12;
const PRUEFTEXT = 'tauchlogbuch-checkin-v1';

const kodierer = new TextEncoder();
const dekodierer = new TextDecoder();

function krypto() {
  const c = globalThis.crypto;
  if (!c?.subtle) {
    throw new Error('Dieser Browser bietet keine Verschlüsselung an (Web Crypto). '
      + 'Über eine unverschlüsselte Verbindung ist der Check-in nicht verfügbar.');
  }
  return c;
}

export function zufall(bytes) {
  return krypto().getRandomValues(new Uint8Array(bytes));
}

export function neuesSalz() {
  return zufall(SALZ_BYTES);
}

/** Passwort + Salz -> Schlüssel. Nicht exportierbar, damit er nicht abfliesst. */
export async function leiteSchluessel(passwort, salz, iterationen = runden) {
  const material = await krypto().subtle.importKey(
    'raw', kodierer.encode(passwort), 'PBKDF2', false, ['deriveKey'],
  );
  return krypto().subtle.deriveKey(
    { name: 'PBKDF2', salt: salz, iterations: iterationen, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,                                   // nicht exportierbar
    ['encrypt', 'decrypt'],
  );
}

/** Beliebige Daten -> {iv, geheim}. Objekte werden als JSON verschlüsselt. */
export async function verschluessle(schluessel, daten) {
  const iv = zufall(IV_BYTES);
  const klartext = daten instanceof ArrayBuffer || ArrayBuffer.isView(daten)
    ? daten
    : kodierer.encode(JSON.stringify(daten));
  const geheim = await krypto().subtle.encrypt({ name: 'AES-GCM', iv }, schluessel, klartext);
  return { iv, geheim: new Uint8Array(geheim) };
}

export async function entschluessle(schluessel, paket) {
  const roh = await krypto().subtle.decrypt(
    { name: 'AES-GCM', iv: paket.iv }, schluessel, paket.geheim,
  );
  return new Uint8Array(roh);
}

export async function entschluessleJson(schluessel, paket) {
  return JSON.parse(dekodierer.decode(await entschluessle(schluessel, paket)));
}

/**
 * Beim Einrichten: Salz, Prüfpaket und Rundenzahl. Nichts davon ist geheim —
 * der Prüftext dient nur dazu, ein falsches Passwort zu erkennen, bevor die
 * Oberfläche mit unlesbaren Daten hantiert.
 */
export async function richteEin(passwort) {
  const salz = neuesSalz();
  const schluessel = await leiteSchluessel(passwort, salz);
  return {
    schluessel,
    tresor: { salz, iterationen: runden, pruefung: await verschluessle(schluessel, PRUEFTEXT) },
  };
}

/** Entsperren. Falsches Passwort scheitert an der Prüfsumme von AES-GCM. */
export async function entsperre(passwort, tresor) {
  const schluessel = await leiteSchluessel(passwort, tresor.salz, tresor.iterationen ?? runden);
  try {
    // Der Prüftext wurde als JSON verschlüsselt und muss auch so gelesen werden.
    // Ein roher Vergleich scheiterte an den Anführungszeichen — und zwar mit
    // derselben Meldung wie ein falsches Passwort.
    if (await entschluessleJson(schluessel, tresor.pruefung) !== PRUEFTEXT) {
      throw new Error('Prüftext passt nicht');
    }
  } catch {
    throw new Error('Passwort falsch.');
  }
  return schluessel;
}

export const KONSTANTEN = { runden, PRUEFTEXT, SALZ_BYTES, IV_BYTES };
